import { DatePipe } from "@angular/common";
import { Component, computed, effect, inject, input, signal } from "@angular/core";
import { FormBuilder, FormControl, ReactiveFormsModule } from "@angular/forms";
import { PopoverDirective } from "@kno2/common/directives";
import { NotificationService } from "@kno2/common/services/notifications";
import { requiredWithMessageValidator } from "@kno2/common/validators";
import { WINDOW_TOKEN } from "@kno2/core/browser";
import { UserIdProofProcessStatus } from "@kno2/core/session/user-profile.model";
import { AdminUsersService, IdProofRequest, ManualClearVerificationPreview, User } from "@kno2/data-access/users";
import { ConfirmationDialogService } from "@kno2/shared/confirmation-dialog";
import { DialogBodyComponent, DialogFooterComponent, DialogService } from "@kno2/shared/dialog";
import { FormInputErrorComponent, FormService } from "@kno2/shared/forms";
import { FormInputComponent } from "@kno2/shared/forms/form-input/form-input.component";
import { FormSelectComponent } from "@kno2/shared/forms/form-select/form-select.component";

interface IdProofingForm {
    passed?: FormControl<boolean>;
}

enum ActionType {
    None = "",
    DownloadIdProof = "download",
    ResetFullIdProofing = "resetFullIdProofing",
    ResetManualIdProofing = "resetManualIdProofing",
    ResyncClearIdProofing = "resyncClearIdProofing",
    PreviewClearVerification = "previewClearVerification",
    LinkClearVerification = "linkClearVerification",
    Submit = "submit"
}

@Component({
    selector: "kno2-admin-users-id-proof-modal",
    imports: [
        ReactiveFormsModule,
        DialogBodyComponent,
        DialogFooterComponent,
        FormSelectComponent,
        FormInputComponent,
        FormInputErrorComponent,
        PopoverDirective,
        DatePipe
    ],
    templateUrl: "./admin-users-id-proof-modal.component.html"
})
export class AdminUsersIdProofModalComponent {
    private static readonly CLEAR_VERIFICATION_BASE_URL = "https://verified.clearme.com/dashboard/verifications?id=";

    private readonly fb = inject(FormBuilder);
    private readonly formService = inject(FormService);
    private readonly dialogService = inject(DialogService);
    private readonly adminUsersService = inject(AdminUsersService);
    private readonly notificationService = inject(NotificationService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly window = inject(WINDOW_TOKEN);

    public readonly userId = input.required<string>();
    protected readonly user = signal<User>(null);
    protected readonly error = signal<string | null>(null);
    protected readonly isLoading = signal<boolean>(false);
    protected readonly currentAction = signal<ActionType>(ActionType.None);

    protected readonly isDownloading = computed(() => this.currentAction() === ActionType.DownloadIdProof);
    protected readonly isResettingFullIdProofing = computed(() => this.currentAction() === ActionType.ResetFullIdProofing);
    protected readonly isResettingManualId = computed(() => this.currentAction() === ActionType.ResetManualIdProofing);
    protected readonly isResyncingClearIdProofing = computed(() => this.currentAction() === ActionType.ResyncClearIdProofing);
    protected readonly isPreviewingClearVerification = computed(() => this.currentAction() === ActionType.PreviewClearVerification);
    protected readonly isLinkingClearVerification = computed(() => this.currentAction() === ActionType.LinkClearVerification);
    protected readonly isSubmitting = computed(() => this.currentAction() === ActionType.Submit);
    protected readonly isAnyActionInProgress = computed(() => this.currentAction() !== ActionType.None);
    protected readonly canResyncClearIdProofing = computed<boolean>(
        () => !!this.user() && !this.user().idProofed && !this.user().idProofClearVerificationSucceeded
    );
    protected readonly canEnterClearVerificationId = computed<boolean>(() => !!this.user() && !this.user().idProofed && !this.user().idProofedAttemptDate);
    protected readonly clearPreview = signal<ManualClearVerificationPreview | null>(null);
    protected readonly isClearEntryOpen = signal<boolean>(false);
    protected readonly canChangeIdProofStatus = computed(
        () => !!this.user() && !this.user().idProofed && this.user().idProofedAttemptDate && this.user().idProofFileName
    );

    protected readonly resetFullIdProofingTooltip =
        "The ID Proofing status will be reset and the user will be prompted to go through the ID Proof process again. Any submitted documents will be deleted.";
    protected readonly resyncClearIdProofingTooltip = "Retrieve updated status from CLEAR when a failed verification has been manually overridden to passed.";
    protected readonly enterClearVerificationIdTooltip =
        "Use when a user completed CLEAR but never returned to finish. Enter the CLEAR verification ID to look it up and link the verification to this user.";
    protected readonly resetManualIdTooltip =
        "Delete submitted document. This will not reset ID Proofing but it will prompt the user to submit a new document.";
    protected readonly statusTooltip =
        "Status changes from Failed to Passed are only allowed when the failure resulted from the user declining CLEAR ID proofing and after the user has provided a document. All other overrides must be done in the CLEAR console.";

    protected readonly form = this.fb.group<IdProofingForm>({
        passed: this.fb.control(null, [requiredWithMessageValidator("ID Proofing Status is required.")])
    });

    protected readonly clearVerificationIdControl = new FormControl<string | null>(null, [
        requiredWithMessageValidator("A CLEAR verification ID is required.")
    ]);

    public readonly initializeFormEffect = effect(async () => {
        const userId = this.userId();
        if (userId) {
            await this.setUser(userId);
            this.initializeForm();
            this.initializeFormEffect.destroy();
        }
    });

    private idProofStatusEnablementEffect = effect(() => {
        if (this.canChangeIdProofStatus()) {
            this.form.get("passed").enable();
        } else {
            this.form.get("passed").disable();
        }
    });

    protected readonly statusOptions = signal([
        ["Passed", true],
        ["Failed", false]
    ]);

    protected readonly idProofProcessStatusMessage = computed<string>(() => {
        const user = this.user();
        if (!user) return "";

        return `${user.firstName} ${user.lastName} ${this.getStatusMessage(user)}`;
    });

    protected readonly idProofProcessStatusMessageClearAdditionalDetail = computed<boolean>(() => this.user()?.idProofClearVerificationSucceeded !== null);

    protected readonly idProofProcessStatusMessageClearAdditionalDetailLink = computed<string>(() =>
        this.user()?.idProofClearVerificationId ? `${AdminUsersIdProofModalComponent.CLEAR_VERIFICATION_BASE_URL}${this.user().idProofClearVerificationId}` : ""
    );

    protected async saveChanges(): Promise<void> {
        this.form.markAllAsTouched();

        const formErrors = this.formService.getErrorValues<string>(this.form);
        if (formErrors.length) {
            return;
        }

        try {
            this.currentAction.set(ActionType.Submit);
            this.error.set(null);

            const formValue = this.form.getRawValue();
            const request: IdProofRequest = {
                userId: this.userId(),
                passed: formValue.passed
            };

            await this.adminUsersService.idProofUserAsync(request);

            this.notificationService.success("User's Identity Proofing Status has been updated.");
            this.dialogService.close(true);
        } catch (error) {
            const errorMessage = error.errorMessages?.[0] || "There was an error updating the user's ID Proofing Status.";
            this.error.set(errorMessage);
        } finally {
            this.currentAction.set(ActionType.None);
        }
    }

    protected async downloadUserIdProof(): Promise<void> {
        try {
            this.currentAction.set(ActionType.DownloadIdProof);
            this.error.set(null);

            const blob = await this.adminUsersService.getUserIdProofAsync(this.userId());
            const url = URL.createObjectURL(blob);
            this.window.open(url, "_blank");
            URL.revokeObjectURL(url);
        } catch {
            this.error.set("There was an error downloading the ID proof document.");
        } finally {
            this.currentAction.set(ActionType.None);
        }
    }

    protected async resetManualIdProofing(): Promise<void> {
        try {
            this.currentAction.set(ActionType.ResetManualIdProofing);
            this.error.set(null);

            await this.adminUsersService.resetManualIdProofingAsync(this.userId());

            this.notificationService.success("User's Manual ID Submission has been reset.");
            this.dialogService.close(true);
        } catch {
            this.error.set("There was an error resetting user's manual ID submission.");
        } finally {
            this.currentAction.set(ActionType.None);
        }
    }

    protected async resetFullIdProofing(): Promise<void> {
        if (this.isAnyActionInProgress()) return;

        await this.confirmationDialogService.open({
            headerText: "Reset Id Proofing",
            bodyText: `
                <p>Restarting will remove this user's current verification status and send them through the verification process again. </p>
                <p>Press OK to Continue.</p>
            `,
            closeOnError: true,
            confirmFn: async () => {
                this.currentAction.set(ActionType.ResetFullIdProofing);
                this.error.set(null);

                await this.adminUsersService.resetFullIdProofingAsync(this.userId());

                this.notificationService.success("User's ID Proofing has been reset.");
                this.dialogService.close(true);
            },
            errorNotificationText: "There was an error resetting user's ID proofing."
        });

        this.currentAction.set(ActionType.None);
    }

    protected async resyncClearIdProofing(): Promise<void> {
        if (this.isAnyActionInProgress()) return;

        try {
            this.currentAction.set(ActionType.ResyncClearIdProofing);
            this.error.set(null);

            await this.adminUsersService.resyncClearIdProofingAsync(this.userId());

            this.notificationService.success("User's ID Proofing has been synced.");

            this.dialogService.close(true);
        } catch (error) {
            const errorMessage = error.errorMessages?.[0] || "There was an error resyncing user's ID proofing.";
            this.error.set(errorMessage);
        } finally {
            this.currentAction.set(ActionType.None);
        }
    }

    protected async lookupClearVerification(): Promise<void> {
        if (this.isAnyActionInProgress()) return;

        const verificationId = (this.clearVerificationIdControl.value ?? "").trim();
        this.clearVerificationIdControl.setValue(verificationId);
        this.clearVerificationIdControl.markAsTouched();
        if (this.clearVerificationIdControl.invalid) return;

        try {
            this.currentAction.set(ActionType.PreviewClearVerification);
            this.error.set(null);

            const preview = await this.adminUsersService.previewClearVerificationAsync(this.userId(), verificationId);

            this.clearPreview.set(preview);
        } catch (error) {
            const errorMessage = error.errorMessages?.[0] || "There was an error looking up the CLEAR verification.";
            this.error.set(errorMessage);
        } finally {
            this.currentAction.set(ActionType.None);
        }
    }

    protected async linkClearVerification(): Promise<void> {
        const preview = this.clearPreview();
        if (!preview || this.isAnyActionInProgress()) return;

        try {
            this.currentAction.set(ActionType.LinkClearVerification);
            this.error.set(null);

            await this.adminUsersService.linkClearVerificationAsync(this.userId(), preview.verificationId);

            this.notificationService.success("The CLEAR verification has been linked to the user.");
            this.dialogService.close(true);
        } catch (error) {
            const errorMessage = error.errorMessages?.[0] || "There was an error linking the CLEAR verification.";
            this.error.set(errorMessage);
        } finally {
            this.currentAction.set(ActionType.None);
        }
    }

    protected resetClearVerificationLookup(): void {
        this.clearPreview.set(null);
        this.clearVerificationIdControl.reset();
        this.error.set(null);
    }

    protected openClearVerificationEntry(): void {
        this.error.set(null);
        this.isClearEntryOpen.set(true);
    }

    protected closeClearVerificationEntry(): void {
        this.isClearEntryOpen.set(false);
        this.resetClearVerificationLookup();
    }

    protected cancel(): void {
        this.dialogService.close(false);
    }

    private async setUser(userId: string): Promise<void> {
        try {
            this.isLoading.set(true);

            const user = await this.adminUsersService.getUserByIdAsync(userId);
            this.user.set(user);
        } catch {
            this.notificationService.error("Failed to load user data.");
        } finally {
            this.isLoading.set(false);
        }
    }

    private initializeForm(): void {
        const user = this.user();
        if (!user) return;

        let passedValue: boolean | null;
        if (
            !user.idProofed &&
            (user.idProofedAttemptDate == null || user.idProofProcessStatus === UserIdProofProcessStatus.AwaitingUserClearDataConfirmation)
        ) {
            passedValue = null;
        } else {
            passedValue = user.idProofed;
        }

        this.form.patchValue({
            passed: passedValue
        });
    }

    private getStatusMessage(user: User): string {
        switch (user.idProofProcessStatus) {
            case UserIdProofProcessStatus.NotIdProofed:
                return "has not been ID proofed.";
            case UserIdProofProcessStatus.ProofedManually:
                return "had ID proof skipped.";
            case UserIdProofProcessStatus.AwaitingManualProofing:
            case UserIdProofProcessStatus.AwaitingProofUpload:
                return `has been id proofed by ${this.getManualIdProofingSource(user)}.`;
            case UserIdProofProcessStatus.ManuallyIdProofed:
                return "has been ID proofed MANUALLY.";
            case UserIdProofProcessStatus.ProofedByEquifax:
                return "has been ID proofed by EQUIFAX.";
            case UserIdProofProcessStatus.ProofedByClear:
                return "has been ID proofed by CLEAR.";
            case UserIdProofProcessStatus.AwaitingUserClearDataConfirmation:
                return "has ID proof through CLEAR in progress.";
            case UserIdProofProcessStatus.Started:
                return "has started ID proofing but not completed it.";
            default:
                return `has an unknown ID proof status: ${user.idProofProcessStatus}`;
        }
    }

    private getManualIdProofingSource(user: User): string {
        return user.idProofClearVerificationSucceeded === false ? "CLEAR" : "EQUIFAX";
    }
}
